import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  currencyFormatter,
  IAgedContactReport,
  IAgedContactReportRow,
  SavedFilterType,
  toDecimal,
} from "shared";
import { toast } from "react-toastify";
import {
  ExportCsv,
  FilterPanelTrigger,
  GridColDef,
  GridCsvExportOptions,
  GridFilterListIcon,
  GridFilterModel,
  GridToolbarProps,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { Download } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import { useApiRequest } from "../../../hooks/useApiRequest";
import { DatePicker } from "../../../../common/Atoms/DatePicker";
import { MultiOrgSelector } from "../MultiOrgSelector";
import { commonConfig, getTogglableColumns } from "../../../lib/datagrid";
import { CurrencySymbolToggle } from "../CurrencySymbolToggle";
import { ApiSavedFilterPanel } from "../../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../../Common/Datagrid/SaveFilterButton";
import { DatagridCard } from "../../Common/Datagrid/DatagridCard";
import { NotesCellButton } from "../../Molecules/NotesDrawer";
import { useApplySavedFilterFromUrl } from "../../../hooks/useApplySavedFilterFromUrl";
import { useOrganisations } from "../../../hooks/useOrganisations";
import { useOrganisationsIncludeDisconnected } from "../../../hooks/useOrganisationsIncludeDisconnected";
import { Select } from "../../../../common/Atoms/Select";
import { CheckBox } from "../../../../common/Atoms/CheckBox";
import { Transition } from "../../../../common/Atoms/Transition";
import {
  buildCurrencyFilterColumn,
  buildDateFilterColumn,
  buildOrganisationsFilterColumn,
  buildSplitCurrenciesFilterColumn,
  getReportFilterValue,
  ReportFilterField,
  reportFilterColumnVisibility,
  reportFilterFieldList,
  withReportFilterValue,
} from "../reportGridFilters";

// augment the props for the toolbar slot
// eslint-disable-next-line quotes
declare module "@mui/x-data-grid"{
  interface ToolbarPropsOverrides {
    showCurrencySymbol: boolean;
    setShowCurrencySymbol: (show: boolean) => void;
  }
}

type ToolbarProps = GridToolbarProps & {
  showCurrencySymbol: boolean;
  setShowCurrencySymbol: (show: boolean) => void;
};

const emptyReport: IAgedContactReport = {
  rows: [],
  agedColumns: {
    debtAgedOne: `0`,
    debtAgedTwo: `1-30`,
    debtAgedThree: `31-60`,
    debtAgedFour: `61-90`,
    debtAgedFive: `90+`,
  },
};

export function AgedContactReport() {
  const apiRequest = useApiRequest();
  const apiRef = useGridApiRef();
  const [reportResponse, setReportResponse] = useState<IAgedContactReport>(emptyReport);
  const [reportLoading, setReportLoading] = useState(false);
  const [showCurrencySymbol, setShowCurrencySymbol] = useState(true);
  // All report-level filters (orgs, dates, currency) live in the grid filter
  // model so they are captured by saved filter presets
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const { data: organisations } = useOrganisations();
  const { data: allOrganisations } = useOrganisationsIncludeDisconnected();

  const possibleCurrencies = useMemo(() => {
    return [...new Set(organisations.flatMap(o => o.possibleCurrencies))];
  }, [organisations]);

  const orgOptions = useMemo(() => {
    return allOrganisations.map(org => ({
      value: org.id,
      label: org.legalName,
    }));
  }, [allOrganisations]);

  const selectedOrgs = useMemo(() => {
    const value = getReportFilterValue(filterModel, ReportFilterField.organisations);

    return Array.isArray(value) ? value as string[] : [];
  }, [filterModel]);

  const startDate = (getReportFilterValue(filterModel, ReportFilterField.startDate) as string | undefined) ?? null;
  const endDate = (getReportFilterValue(filterModel, ReportFilterField.endDate) as string | undefined) ?? null;
  const selectedCurrency = (getReportFilterValue(filterModel, ReportFilterField.currency) as string | undefined) ?? null;
  const splitValue = getReportFilterValue(filterModel, ReportFilterField.splitCurrencies);
  // A specific currency supersedes splitting by currency
  const splitCurrencies = !selectedCurrency && (splitValue === true || splitValue === `true`);

  // Serialised so the fetch effect only re-runs when the report query itself
  // changes, not on unrelated column filter edits
  const reportQueryKey = JSON.stringify({
    orgs: selectedOrgs,
    startDate,
    endDate,
    currency: selectedCurrency,
    splitCurrencies,
  });

  useEffect(() => {
    const params = JSON.parse(reportQueryKey) as {
      orgs: string[];
      startDate: string | null;
      endDate: string | null;
      currency: string | null;
      splitCurrencies: boolean;
    };

    async function fetchReport() {
      try {
        setReportLoading(true);

        const response = await apiRequest
          .get<IAgedContactReport>(`/reports/aged`, {
            params,
          });

        setReportResponse(response.data);
        setReportLoading(false);
      }
      catch (err) {
        setReportLoading(false);
        console.error(`Error fetching aged contact report`, err);
        toast.error(`Could not fetch aged contact report. ${err?.response?.data?.toString() || ``}`);
      }
    }

    if (params.orgs.length > 0) {
      fetchReport();
    }

    if (params.orgs.length === 0) {
      setReportResponse(emptyReport);

      setReportLoading(false);
    }
  }, [reportQueryKey, apiRequest]);

  useApplySavedFilterFromUrl(apiRef, SavedFilterType.ReportAged, {
    defaultColumnVisibilityModel: reportFilterColumnVisibility,
  });

  function setSelectedOrgs(orgs: string[]) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.organisations, `isAnyOf`, orgs));
  }

  function setStartDate(value: string | null) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.startDate, `is`, value));
  }

  function setEndDate(value: string | null) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.endDate, `is`, value));
  }

  function handleSetSelectedCurrency(currency: string | null) {
    setFilterModel(model => withReportFilterValue(
      withReportFilterValue(model, ReportFilterField.splitCurrencies, `is`, false),
      ReportFilterField.currency,
      `is`,
      currency,
    ));
  }

  function setSplitCurrencies(split: boolean) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.splitCurrencies, `is`, split));
  }

  // Add the currency symbol flag in the row state to
  const columnsDef: GridColDef<IAgedContactReportRow>[] = useMemo(() => {
    return [
      {
        field: `contactName`,
        headerName: `Contact Name`,
        minWidth: 130,
      },
      {
        field: `organisationName`,
        headerName: `Entity Name`,
        minWidth: 130,
      },
      {
        field: `externalAggregateNames`,
        // we have no specific org at this point and could be blending xero and qbo
        // we'll need to hedge our bets for now
        headerName: `Group/Type`,
        minWidth: 130,
        valueGetter: (value, row) => row.externalAggregateNames?.join(`, `) || undefined,
      },
      {
        field: `debtAgedOne`,
        type: `number`,
        headerName: `Not due`,
        valueGetter: (value, row) => toDecimal(row.debtAgedOne),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `debtAgedTwo`,
        type: `number`,
        headerName: `1-30 days`,
        valueGetter: (value, row) => toDecimal(row.debtAgedTwo),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `debtAgedThree`,
        type: `number`,
        headerName: `31-60 days`,
        valueGetter: (value, row) => toDecimal(row.debtAgedThree),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `debtAgedFour`,
        type: `number`,
        headerName: `61-90 days`,
        valueGetter: (value, row) => toDecimal(row.debtAgedFour),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `debtAgedFive`,
        type: `number`,
        headerName: `90+ days`,
        valueGetter: (value, row) => toDecimal(row.debtAgedFive),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `totalOutstandingCents`,
        type: `number`,
        headerName: `Outstanding`,
        valueGetter: (value, row) => toDecimal(row.totalOutstandingCents),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `totalOverdueCents`,
        type: `number`,
        headerName: `Overdue`,
        valueGetter: (value, row) => toDecimal(row.totalOverdueCents),
        valueFormatter: (value: number, row) => {
          if (showCurrencySymbol) {
            const currency = row.currency;

            return currencyFormatter(currency)(value);
          }

          return value.toFixed(2);
        },
        filterable: false,
      },
      {
        field: `unpaidInvoiceCount`,
        headerName: `No. of Unpaid Invoices`,
        type: `number`,
      },
      {
        field: `averageDaysToPay`,
        headerName: `Avg Days to Pay`,
        type: `number`,
        valueGetter: (value, row) => row.averageDaysToPay,
        valueFormatter: value => value === undefined ? `` : `${value} days`,
      },
      {
        field: `paidInvoiceCount`,
        headerName: `No. of Paid Invoices`,
        type: `number`,
        valueGetter: (value, row) => row.paidInvoiceCount,
        valueFormatter: value => value === undefined ? `` : value,
      },
      {
        field: `mostRecentNote`,
        headerName: `Most Recent Note`,
        type: `string`,
        valueGetter: (value, row) => row.mostRecentNote,
        filterable: false,
      },
      {
        field: `notes`,
        headerName: `Notes`,
        width: 70,
        sortable: false,
        filterable: false,
        disableExport: true,
        align: `center`,
        headerAlign: `center`,
        renderCell: ({ row }) => (
          <NotesCellButton resource={ { type: `contact`, id: row.contactId, organisationId: row.organisationId } } />
        ),
      },
      {
        field: `portalUrl`,
        headerName: `Portal URL`,
        type: `string`,
        valueGetter: (value, row) => row.portalUrl,
        filterable: false,
        renderCell: value => {
          return (
            <a
              rel={ `noopener noreferrer` }
              target={ `_blank` }
              href={ value.value }
              className={ `text-blue-600 underline` }
            >
              { `View Portal` }
            </a>
          );
        },
      },
      // Hidden columns carrying the report-level filters in the filter model
      buildOrganisationsFilterColumn(orgOptions),
      buildDateFilterColumn(ReportFilterField.startDate, `Start Date`),
      buildDateFilterColumn(ReportFilterField.endDate, `End Date`),
      buildCurrencyFilterColumn(possibleCurrencies),
      buildSplitCurrenciesFilterColumn(),
    ];
  }, [showCurrencySymbol, orgOptions, possibleCurrencies]);

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 180; // px for the header

  return (
    <div className={ `flex flex-col h-full` }
      style={ { height: tableHeight } }>
      <div className={ `flex gap-4 mb-4 items-end` }>
        <div className={ `w-1/3` }>
          <MultiOrgSelector
            onChange={ setSelectedOrgs }
            selected={ selectedOrgs }
          />
        </div>
        <DatePicker
          value={ startDate }
          onChange={ ({ value }) => setStartDate(value) }
          placeholder={ `Select start date` }
        />
        <DatePicker
          value={ endDate }
          onChange={ ({ value }) => setEndDate(value) }
          placeholder={ `Select end date` }
        />
        <div>
          <Select
            selected={ selectedCurrency }
            onChange={ value => handleSetSelectedCurrency(value.value as string) }
            nullable
            label={ `Currency Filter` }
            options={ possibleCurrencies.map(c => ({ label: c, value: c })) }
            emptyText={ `All Currencies` }
            helpIcon={ `All currencies will include all invoices converted to your organisation currency. Specific currencies only show invoices in that currency.` }
          />
        </div>

        <Transition
          show={ !selectedCurrency }
          speed={ `slow` }
        >
          <div className={ `pb-2` }>
            <CheckBox
              checked={ splitCurrencies }
              onChange={ e => setSplitCurrencies(e.value) }
              label={ `Split Currencies` }
              helpIcon={ `Instead of converting to home currencies, each currency & contact combination will be shown as a separate row.` }
            />
          </div>
        </Transition>
      </div>
      <DatagridCard className={ `flex-1` }>
        <DataGridPro
          apiRef={ apiRef }
          columns={ columnsDef }
          rows={ reportResponse.rows }
          loading={ reportLoading }
          getRowId={ row => row.id }
          rowHeight={ 38 }
          filterModel={ filterModel }
          onFilterModelChange={ setFilterModel }
          initialState={ {
            columns: {
              columnVisibilityModel: reportFilterColumnVisibility,
            },
          } }
          slots={ { toolbar: CustomToolbar, filterPanel: ApiSavedFilterPanel } }
          showToolbar
          slotProps={ {
            toolbar: {
              showCurrencySymbol,
              setShowCurrencySymbol,
            },
            columnsManagement: {
              getTogglableColumns: columns => getTogglableColumns(columns, reportFilterFieldList),
            },
            filterPanel: {
              savedFilterType: SavedFilterType.ReportAged,
            },
          } }
          sx={ {
            ...commonConfig.headerStyle,
          } }
        />
      </DatagridCard>
    </div>
  );
}

function CustomToolbar({
  showCurrencySymbol,
  setShowCurrencySymbol,
}: ToolbarProps) {
  const csvOptions: GridCsvExportOptions = {
    fileName: `aged_ar_contact_report`,
  };

  return (
    <Toolbar>
      <Tooltip title={ `Filters` }>
        <FilterPanelTrigger render={ <ToolbarButton /> }>
          <GridFilterListIcon fontSize={ `medium` } />
        </FilterPanelTrigger>
      </Tooltip>
      <SaveFilterButton type={ SavedFilterType.ReportAged } />
      <div className={ `flex justify-end w-full` }>
        <CurrencySymbolToggle
          showCurrencySymbol={ showCurrencySymbol }
          setShowCurrencySymbol={ setShowCurrencySymbol }
        />
        <ExportCsv
          render={ <ToolbarButton /> }
          options={ csvOptions }
        >
          <Download fontSize={ `medium` }/>
        </ExportCsv>
      </div>
    </Toolbar>
  );
}
